import { Box, Typography } from "@mui/material";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import { useAppSelector } from "app/hooks";
import { Toggle } from "components/FormControls/Toggle";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { FieldArray } from "formik";
import { useMessage } from "hooks/intl";
import { Dispatch, SetStateAction } from "react";
import {
  selectAvaiableTranslations,
  selectDefaultLanguageData,
} from "reduxState/translations/translationsSlice";
import { ConditionType, TierDetailsType } from "types/tierSets";
import { useStyles } from "../TiersForm.styles";
import { Rewards } from "./Rewards/Rewards";
import { TiersTabs } from "./TiersTabs/TiersTabs";
import { Translations } from "./Translations";

interface Props {
  activeTier: number;
  setActiveTier: Dispatch<SetStateAction<number>>;
  tiers: TierDetailsType[];
  conditions: ConditionType[];
  setFieldValue: (field: string, value: any) => void;
}

export const BasicInformation = ({
  activeTier,
  setActiveTier,
  tiers,
  conditions,
  setFieldValue,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const availableLanguages = useAppSelector(selectAvaiableTranslations);
  const areAllAvailableTranslationsAdded =
    availableLanguages.length ===
    Object.keys(tiers?.[activeTier]?.translations || {}).length;

  const maxTiers = 20;
  const isMaxTiersExceeded = tiers.length >= maxTiers;
  const isDefaultTier = tiers[activeTier]?.isDefault;

  return (
    <OLPaper className={classes.OLPaper}>
      <section>
        <FieldArray
          name="tiers"
          render={({ remove, push, form: { values, errors } }) => {
            const { tiers }: { tiers: TierDetailsType[] } = values;

            return (
              <Box className={classes.tabs}>
                <TiersTabs
                  activeTier={activeTier}
                  setActiveTier={setActiveTier}
                  tiers={tiers}
                  defaultLang={defaultLang}
                  remove={remove}
                  errors={errors}
                />
                <OLButton
                  color="primary"
                  variant="contained"
                  data-test-id="add-tier"
                  disabled={isMaxTiersExceeded}
                  className={classes.addTier}
                  size="small"
                  startIcon={<PlusIcon />}
                  onClick={() => {
                    push({
                      active: true,
                      translations: {
                        [defaultLang.code]: {
                          name: `${message({ id: "tier" }).toUpperCase()} #${
                            tiers.length + 1
                          }`,
                          description: "",
                          locale: defaultLang.code,
                        },
                      },
                      rewards: [],
                      conditions: conditions.map((condition) => ({
                        attribute: condition.attribute,
                        conditionId: condition.id,
                        walletType: condition.walletType,
                        eventName: condition.eventName,
                        value: null,
                      })),
                    });

                    setActiveTier(tiers.length);
                  }}
                >
                  {message({ id: "addTier" })}
                </OLButton>
              </Box>
            );
          }}
        />
      </section>
      <section className={classes.translationContainer}>
        {availableLanguages && (
          <Translations
            activeTier={activeTier}
            tiers={tiers}
            translations={availableLanguages}
            areAllAvailableTranslationsAdded={areAllAvailableTranslationsAdded}
            setFieldValue={setFieldValue}
          />
        )}
      </section>
      <section className={classes.isActive}>
        <Toggle
          label={message({ id: "active" })}
          name={`tiers[${activeTier}].active`}
          data-test-id="active"
          disabled={isDefaultTier}
        />
      </section>
      <Typography
        className={classes.sectionHeader}
        mb={8}
        mt={areAllAvailableTranslationsAdded ? 12 : 0}
      >
        {message({ id: "tierBenefits" })}
      </Typography>
      <section>
        <Rewards activeTier={activeTier} />
      </section>
    </OLPaper>
  );
};
